import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
    Button,
    Card,
    Col,
    Input, Modal, Row,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip
} from 'antd'
import { CopyOutlined, PlusSquareOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { CreateStore, HubListItem } from '@a4b/api/src/modules/Monetisation/Puja/types'
import { CustomLoader } from '@/components/Loader'
import { useAppContext } from '@/components/AppContext'
import { A4B_APPS, SHOW_DEPRECATION_BANNER_CTA } from '@/utils/constants'
import { getMonetisationPujaApi } from '@/utils/helper'


interface Props {
    templeList: CreateStore[]
    onStatusChange: (item: CreateStore) => void
    archiveStore: (storeId: string) => void
    unarchiveStore: (storeId: string) => void
    cloneStore: (storeId: string, hubId: string) => Promise<void>
    activeCloneId: string | undefined
    sortBy: 'created_at' | 'position',
    isArchive?: 'true' | 'false',
    isActive?: 'true' | 'false',
    updateFilters: (updateType: 'sortBy' | 'isArchive' | 'isActive' | 'serviceType' | 'title' | 'id', value?: 'true' | 'false' | 'created_at' | 'position' | 'pooja' | 'offerings' | 'promoter' | 'mandir_connect' | 'all' | string) => void,
    loading: boolean,
    onPageChange: any
    pagination: any
    isLastPage: boolean
}



const Container = styled(Card)`
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex-grow: 1;
  }
`
const Label = styled.span`
  font-size: 14px;
  padding-left: 10px;
`
enum MODAL_TYPE {
    CLONE = 'Clone',
    ARCHIVE = "Archive",
    UNARCHIVE = "Unarchive"
}
const StoreListTable: React.FC<Props> = ({
    templeList,
    onStatusChange,
    unarchiveStore,
    archiveStore,
    cloneStore,
    activeCloneId,
    sortBy,
    isArchive,
    isActive,
    updateFilters,
    loading,
    pagination,
    onPageChange,
    isLastPage
}) => {
    const [searchParams] = useSearchParams();
    const { app, networkInstance } = useAppContext()
    const isSrimandir = app === A4B_APPS.SRIMANDIR
    const [dataSource, setDataSource] = useState(templeList);
    const [title, setTitle] = useState<string>(searchParams?.get('title') || '');
    const [modelConfig, setModelConfig] = useState<{ type: MODAL_TYPE, id: string, hubId?: string, callback: (id: string, hubId: string) => void }>()
    const monetisationPujaApi = getMonetisationPujaApi(app, networkInstance);
    const [hubList, setHubList] = useState<HubListItem[]>()
    const [isLoadingHubList, setIsLoadingHubList] = useState(false)
    const [tooltipTitle, setTooltipTitle] = useState('Copy');
    const [tooltipVisible, setTooltipVisible] = useState(false)

    const handleCopy = (id: string) => {
        navigator.clipboard.writeText(id);
        setTooltipTitle('Copied');
        setTimeout(() => setTooltipTitle('Copy'), 2000);
    };

    const handlePrevPage = () => {
        if (pagination.current > 1) {
            onPageChange(pagination.current - 1);
        }
    };

    const handleNextPage = () => {
        onPageChange(pagination.current + 1);
    };

    const getHubList = useCallback((async () => {
        setIsLoadingHubList(true)
        try {
            const hubList = await monetisationPujaApi.hub.getHubList({
                limit: 1000,
                offset: 0,
            })

            setHubList(hubList?.data?.data?.hubs)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoadingHubList(false)
        }
    }), [monetisationPujaApi.hub])

    useEffect(() => {
        if (hubList) {
            return
        }
        if (modelConfig?.type === MODAL_TYPE.CLONE) {
            getHubList()
        }
    }, [modelConfig, getHubList, hubList])

    useEffect(() => { setDataSource(templeList) }, [templeList])

    const handleSearchTitleChange = useCallback((e: any) => {
        const currValue = e.target.value;
        setTitle(currValue);

        if (currValue) {
            const filteredData = templeList?.filter((entry) =>
                entry?.title?.toLowerCase().includes(currValue?.toLowerCase())
            );
            setDataSource(filteredData);
        } else {
            setDataSource(templeList);
            updateFilters('title', '');
        }
    }, [templeList, updateFilters]);

    const filterSearchTitleInput = useCallback(() => (
        <Tooltip
            title="Search works for current page"
            visible={tooltipVisible}
            placement="topLeft"
        >
            <Input
                placeholder="Search by Title"
                value={title}
                //onPressEnter={(e) => updateFilters('title', e.currentTarget.value)}
                onChange={handleSearchTitleChange}
                onFocus={() => {
                    setTooltipVisible(true);
                    setTimeout(() => setTooltipVisible(false), 3000); // Optional: auto-hide after 3s
                }}
                onBlur={() => setTooltipVisible(false)}
                allowClear
            />
        </Tooltip>
    ), [title, templeList, updateFilters, tooltipVisible, setTooltipVisible]);

    const filterSearchIdInput = useCallback(() => (
        <Input
            placeholder="Search by Store ID"
            onPressEnter={(e) => {
                onPageChange(1); // reset to page 1 (offset 0)
                updateFilters('id', e.currentTarget.value);
            }}
            onChange={(e) => {
                if (e.target.value === '') {
                    onPageChange(1);
                    updateFilters('id', '');
                }
            }}
            allowClear
        />
    ), [updateFilters]);

    const dataColumns = [
        {
            title: <div style={{ display: 'flex', flexDirection: 'column' }}> <span>Title</span> <div>{filterSearchTitleInput()}</div></div>,
            dataIndex: 'title',
            key: 'no_of_items',
            width: '28%',
        },
        {
            title: <div style={{ display: 'flex', flexDirection: 'column' }}> <span>Store Id</span> <div>{filterSearchIdInput()}</div></div>,
            dataIndex: 'id',
            key: 'id',
            width: '20%',
            ellipsis: true,
            render: (id: string) => (
                <div>
                    <Tooltip title={tooltipTitle}>
                        <span style={{ cursor: 'pointer', marginRight: '8px', color: '#69b1ff' }} onClick={() => handleCopy(id)}>
                            <CopyOutlined />
                        </span>
                    </Tooltip>
                    <Tooltip placement="topLeft" title={id}>
                        {id}
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'No of Items',
            dataIndex: 'no_of_items',
            key: 'no_of_items',
            width: '10%',
            render: (text: string) => <span>{text}</span>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '10%',
            render: (text: string, record: any) => {
                return (
                    <span>
                        <Label>{record.active ? <Tag color='green'>Active</Tag> : <Tag color='red'>In Active</Tag>}</Label>
                    </span>
                )
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: '30%',
            render: (text: any, record: any, index: any) => {
                return (
                    <Row style={{ alignItems: 'center' }}>
                        <Col span={5}>
                            <Switch
                                checked={record.active}
                                onChange={() => onStatusChange(record)}
                            />
                        </Col>
                        <Col span={4}>
                            <Link
                                to={isSrimandir ? `/monetisation/puja/temple/create?edit=true&store_id=${record.id}` :
                                    `/astro/puja/temple/create?edit=true&store_id=${record.id}`
                                }
                            >
                                <Button type='link'>Edit</Button>
                            </Link>
                        </Col>

                        <Col span={6}>
                            {
                                record.archived ?
                                    <Button onClick={() => setModelConfig({ type: MODAL_TYPE.UNARCHIVE, id: record.id, hubId: undefined, callback: unarchiveStore })} type='link'> Unarchive </Button>
                                    :
                                    <Button onClick={() => setModelConfig({ type: MODAL_TYPE.ARCHIVE, id: record.id, hubId: undefined, callback: archiveStore })} type='link' > Archive </Button>
                            }
                        </Col>
                        <Col span={4}>
                            <Button disabled={SHOW_DEPRECATION_BANNER_CTA} size='small' onClick={() => setModelConfig({ type: MODAL_TYPE.CLONE, id: record.id, hubId: '', callback: cloneStore })} loading={activeCloneId === record.id} > Clone </Button>
                        </Col>
                    </Row>
                )
            },
        },
    ]

    return (
        <div>
            <Container
                title='Manage Stores'
                style={{ width: '100%' }}
                extra={
                    <Row style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Col>
                            <label>Filter by: </label>
                            <Select placeholder="Is archived" allowClear value={isArchive} onChange={(value) => { updateFilters('isArchive', value) }} style={{ width: '120px', paddingLeft: '10px' }}>
                                <Select.Option value="true">Archived</Select.Option>
                                <Select.Option value="false">Unarchived</Select.Option>
                            </Select>
                            <Select placeholder="Status" value={isActive} onChange={(value: any) => { updateFilters('isActive', value) }} style={{ width: '120px', paddingLeft: '10px' }}>
                                <Select.Option value="true">Active</Select.Option>
                                <Select.Option value="false">Inactive</Select.Option>
                            </Select>
                        </Col>
                        <Col>
                            <label style={{ paddingLeft: '10px' }}>Sort by: </label>
                            <Select value={sortBy} onChange={(value) => { updateFilters('sortBy', value) }} style={{ width: '120px' }}>
                                <Select.Option value="created_at">Created at</Select.Option>
                                <Select.Option value="active">Active</Select.Option>
                                <Select.Option value="position">Position</Select.Option>
                            </Select>
                        </Col>
                        <Col style={{ paddingLeft: '10px' }}>
                            <Button type='primary' disabled={SHOW_DEPRECATION_BANNER_CTA}>
                                <Link to={isSrimandir ? '/monetisation/puja/temple/create' : '/astro/puja/temple/create'}>
                                    <PlusSquareOutlined /> Add new Store
                                </Link>
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <Space direction={'vertical'} style={{ width: '100%' }}></Space>
                {
                    modelConfig &&
                    <Modal
                        title={modelConfig.type}
                        visible={true}
                        onOk={() => {
                            modelConfig.callback(modelConfig.id, modelConfig.hubId || '')
                            setModelConfig(undefined)
                        }}
                        onCancel={() => { setModelConfig(undefined) }}

                    >
                        <p style={{ textAlign: "center" }}>
                            <b>
                                {
                                    modelConfig.type === MODAL_TYPE.CLONE ?
                                        "Are you sure you want to Clone ?" : modelConfig.type === MODAL_TYPE.ARCHIVE ?
                                            "Are you sure you want to Archive ?" :
                                            "Are you sure you want to Unarchive ?"
                                }
                            </b></p>
                        {
                            modelConfig.hubId !== undefined &&
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                                <p>
                                    Please choose the hub for which you want to clone this store
                                </p>
                                {isLoadingHubList ?
                                    <CustomLoader /> :
                                    <Select onChange={(id) => setModelConfig({ ...modelConfig, hubId: id } as typeof modelConfig)} aria-required style={{ width: '350px' }} placeholder="Select hub" >
                                        {hubList?.map((hub: HubListItem) => (
                                            <Select.Option key={hub?.id} value={hub?.id}>{hub?.name}</Select.Option>
                                        ))}
                                    </Select>
                                }
                            </div>
                        }
                    </Modal>
                }
                <Row>
                    <Col span={28}>
                        <Table
                            sticky={true}
                            rowKey={(record: CreateStore) => record?.id}
                            columns={dataColumns}
                            dataSource={dataSource}
                            loading={{
                                spinning: loading,
                                indicator: <Spin indicator={<CustomLoader />} />,
                            }}
                            pagination={false}
                        />
                    </Col>
                </Row>
                <div>
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'right' }}>
                        <Button
                            onClick={handlePrevPage}
                            disabled={pagination.current === 1 || loading}
                        >
                            Previous
                        </Button>
                        <Button
                            onClick={handleNextPage}
                            style={{ marginLeft: '8px' }}
                            disabled={isLastPage || loading}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </Container>
        </div>
    )
}

export default StoreListTable